// NDIS page copy. Deliberate wording rules, agreed with Josh:
//  - "may help", never "will" or "reduces"
//  - no claim to be a registered NDIS provider, and no promise about funding
//  - participant experiences are quoted as their own words, never as claims about vimbal
import videoNdis from "../vid/vimbal_ndis_wheelchair.mp4";
import { colors } from "../styles/theme";

export const ndis = {
  eyebrow: "NDIS",
  titleLead: "Learning support that",
  titleEmphasis: "may help you focus, read and remember.",
  intro:
    "The National Disability Insurance Scheme (NDIS) is Australia's government-funded support for people with disability. vimbal can be purchased under a participant's plan. It is software you use in a headset: it quiets the outside world, helps the mind settle, then guides you through material at your own pace. Participants use it at home, in their own time, with support if they want it.",

  plan: {
    title: "Using vimbal under your plan",
    status:
      "vimbal is not yet a registered NDIS provider. Registration is underway.",
    managed:
      "Self-managed and plan-managed participants can purchase vimbal now. If your plan is NDIA-managed, speak to your planner first, as approval may be needed.",
    lineItemLabel: "Participants may use vimbal under",
    lineItem: "Communication and Information AT - Computer / Device interfaces",
    lineItemCode: "05_221300111_0124_1_2",
    lineItemNote: "ICT interfaces designed specifically to address disability need.",
    caution:
      "Whether vimbal suits your plan is a decision for you, your plan manager or your support coordinator. We can't advise on funding or guarantee any outcome.",
  },

  included: {
    title: "What's included, for 12 months",
    items: [
      { label: "The platform", body: "Access to vimbal, with software updates and new content through the year." },
      { label: "The hardware", body: "A VR headset and noise-cancelling headphones, leased for the year and returned at the end." },
      { label: "Setup", body: "Your device configured for how you like to work, sound, color and pace included." },
      { label: "Training", body: "A walk-through to get started, for you and anyone supporting you." },
      { label: "Support", body: "Technical help weekdays 9am–5pm AEST, repairs or replacement of faulty equipment, and an annual hardware check." },
    ],
  },

  mayHelp: {
    title: "What it may help with",
    items: [
      { label: "Settling before you start", body: "The headset shuts out noise and distraction, which may help you feel calmer." },
      { label: "Taking information in", body: "A calmer mind may make material easier to absorb." },
      { label: "Reading for longer", body: "Sound, color and pace move with you, which may help you hold focus." },
      { label: "Working towards plan goals", body: "Learning and skill development, daily living, and getting ready for study or work." },
    ],
    note: "Everyone is different and results vary. vimbal is a learning tool, not a treatment, and it does not replace therapy or clinical care.",
  },

  // One participant, in his own words. Numbers are his self-reported scores, not measurements
  // by vimbal, and are presented as his experience rather than as an outcome we promise.
  story: {
    title: "Participant experiences",
    who: "Neurodivergent adult with a master's degree in theology",
    detail: "13 diagnoses, including autism, ADHD, anxiety, depression and PTSD",
    quotes: [
      "I read 5,200 words of theological material in 14.5 minutes and came out and spoke about it like I remembered it. Usually 5,200 words would take two days of reading and note taking.",
      "My anxiety is usually 7–8 out of 10. In vimbal it was 1–2. Afterwards I was 3.",
    ],
    afterTitle: "Afterwards",
    after: [
      { when: "Two hours later", what: "His family said he was sitting on his phone smiling. They hadn't seen that in 18 months." },
      { when: "The next morning", what: "He came into their room laughing and showing them videos. He hadn't done that in five years." },
      { when: "Six months on", what: "He said his PTSD flashbacks no longer come back with the emotional triggers they used to." },
    ],
    disclaimer:
      "One participant's experience, shared with permission and anonymized. Self-reported, not a clinical measure. Individual results vary.",
  },

  // A second participant, in his own words.
  second: {
    who: "PhD neuroscientist with ADHD",
    detail: "Medical researcher of 17 years, 45",
    quotes: [
      "The focus was the most incredible thing for me. I was able to retain attention. That was huge for me.",
      "Focusing on a task can take a Herculean effort, and that's why it felt strange to me. My brain was behaving differently.",
      "The only other time I felt something similar was the very first time I took ADHD meds. That sort of mental clarity.",
    ],
  },

  suitability: {
    title: "Is it right for you?",
    items: [
      "Sessions are short, and you set the pace.",
      "Sound, color and brightness can all be turned down, and the background tones can be switched off entirely. Tell us if low tones set off migraines or ringing in your ears, and we'll start with them off.",
      "You can use it alone, or with a support worker or family member.",
      "It may not suit you if you have photosensitive epilepsy, get motion sick easily, or have had recent eye surgery. Check with your GP if you're unsure.",
    ],
  },

  coordinators: {
    title: "For support coordinators and plan managers",
    body:
      "We can provide a service agreement, itemised quotes for the platform subscription and the hardware lease, and the line item details for your records.",
    cta: "Contact us for a price guide",
  },

  steps: {
    title: "Getting started",
    items: [
      { n: "01", label: "Register your interest", body: "Tell us it's for an NDIS plan and we'll get back to you." },
      { n: "02", label: "We answer the questions", body: "A price guide and anything you, your coordinator or plan manager needs." },
      { n: "03", label: "Setup and training", body: "Once it's agreed, the hardware arrives and we walk you through it." },
    ],
  },

  closing: {
    title: "Start with a conversation,",
    emphasis: "not a commitment.",
    body: "Tell us about your plan and your goals. We'll tell you honestly whether vimbal may help.",
  },
};

// The NDIS page on the shared vertical template, so a plan manager or support coordinator
// who has read nothing else on the site gets the same shape as every other page: hook,
// evidence, the problem, then the plan detail. Everything in `ndis` above still renders,
// below the evidence, unchanged.
export const ndisVertical = {
  slug: "ndis",
  nav: "NDIS",
  accent: colors.walnut,
  eyebrow: "NDIS",
  titleLead: "Two days of reading,",
  titleEmphasis: "done in fourteen minutes.",
  body:
    "The National Disability Insurance Scheme (NDIS) is Australia's government-funded support for people with disability. vimbal can be purchased under a participant's plan: software used in a headset at home that quiets the outside world, settles the mind, then guides you through material at your own pace.",
  video: videoNdis,
  cta: "Register interest",
  // The business case, not personal metrics: the two things a plan manager checks first.
  heroFigures: [
    { value: "One line item", label: "self-managed and plan-managed participants can buy now" },
    { value: "12 months", label: "headset, headphones, setup and support, all included" },
  ],
  problemTitle: "The plan funds the goal. The material still has to get through.",
  // Problem to answer, left to right, the way a plan manager reads. The right-hand lines are
  // the existing "may help" copy, so the wording rules above hold.
  pairLabels: { left: "The problem", right: "With vimbal" },
  pairs: [
    { problem: "Material arrives as dense text in a noisy room, and little of it reaches memory", answer: "The headset shuts out noise and distraction, which may help the participant feel calmer and take the material in" },
    { problem: "Much of a session goes on settling before any learning starts", answer: "The mind settles first, then the reading starts, at a pace the participant sets" },
    { problem: "Holding attention on a page for long is hard work", answer: "Sound, color and pace move with the reader, which may help them hold focus" },
    { problem: "What works with a support worker in the room is hard to repeat alone", answer: "Used at home, in the participant's own time, alone or with a support worker or family member" },
    { problem: "Plan goals in study, daily living or work stall at the reading", answer: "Learning and skill development, daily living, and getting ready for study or work, on the participant's own material" },
  ],
  pairsNote: ndis.mayHelp.note,
  quote: "Two hours later, his family said he was sitting on his phone smiling. They hadn't seen that in 18 months.",
  note: "An NDIS participant's family, after a session. Shared with permission and anonymized.",
  ndis: true,
  calculator: {
    title: "How long would it take you?",
    words: 5200,
    wordsLabel: "Words to read (a 19-page document)",
    typical: 110,
    vimbal: 350,
  },
  uses: ["Capacity building", "Daily living skills", "Study and training", "Getting ready for work"],
  delivery: "Used at home, in your own time, alone or with a support worker or family member. Sessions are short and you set the pace.",
  research: ndis.mayHelp.note,
  closing: ndis.closing,
};
